import { useEffect, useState, useCallback, useRef, memo, useMemo } from "react";
import { useSelector } from "react-redux";
import { isPlainObject } from "lodash";

import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";

import {
  Stack,
  Box,
  Typography,
  TextField,
  Grid2 as Grid,
  FormControlLabel,
  Radio,
  RadioGroup,
  CircularProgress,
  Autocomplete,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

import { getSections } from "../../../../../../permitForms/permitFormHelpers";
import {
  formatMoneyValue,
  formatPercentageValue,
  parseDollarStrToNumber,
} from "../../../../../../../functions/permitFormHelpers";
import {
  hiddenFields,
  filter_number_input_values,
  unitNumMapping,
  totalFields,
} from "../../../../../../../data/permitFormsData";
import {
  bondingLabel,
  customLabels,
  feeSimpleLabel,
  mortgageLabel,
  privateProviderLabel,
  projectLabel,
} from "../../../../../../../data/buildingPermit/fieldDatas";
import RenderPrivateProvider from "./savedProjects/RenderPrivateProvider";
import RenderProjectContact from "./savedProjects/RenderProjectContact";
import RenderDesignPro from "./savedProjects/RenderDesignPro";
import RenderFeeSimple from "./savedProjects/RenderFeeSimple";
import RenderBonding from "./savedProjects/RenderBonding";
import RenderMortgage from "./savedProjects/RenderMortgage";
import RenderTermite from "./savedProjects/RenderTermite";
import { getTemplateCategoryConfig } from "../../../../../../permitForms/utils/templateCategoryHelpers";
import { scrollPreviewFieldIntoView } from "./previewFieldScroll";
import { updatePreviewFieldInstanceLabel } from "./previewFieldLabel";
import { isRightPreviewFormField } from "./previewFieldSelection";
import GenericTablePreview, {
  isGenericTablePreviewField,
} from "./GenericTablePreview";

const toTitleCase = (str = "") =>
  String(str)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

const TEMPLATE_CATEGORY_SECTION_PREFIX = "template-category:";
const TEMPLATE_SELECTOR_FIELD_TYPES = new Set([
  "privateProvider",
  "project_contact_details",
  "designProfessional",
  "feeSimple",
  "bonding",
  "mortgage",
]);
const TEMPLATE_CATEGORY_FIELD_LABELS = {
  ...privateProviderLabel,
  ...projectLabel,
  ...customLabels,
  ...feeSimpleLabel,
  ...bondingLabel,
  ...mortgageLabel,
};

export const getPreviewFieldLabel = (field) => {
  const fieldName = String(field?.name || "").trim();
  const assignedLabel = [field?.field_label, field?.label]
    .map((label) => String(label || "").trim())
    .find((label) => label && label !== fieldName);

  return (
    assignedLabel ||
    TEMPLATE_CATEGORY_FIELD_LABELS[fieldName] ||
    toTitleCase(fieldName) ||
    fieldName
  );
};

export const normalizeTemplateCategoryPreviewField = (field) => {
  const templateCategory = getTemplateCategoryConfig(field?.category_id);

  if (!templateCategory) {
    return field;
  }

  return {
    ...field,
    label: getPreviewFieldLabel(field),
    ...(TEMPLATE_SELECTOR_FIELD_TYPES.has(field?.type)
      ? {
          field_type: "text",
          type: "text",
        }
      : {}),
  };
};

export const getPreviewSectionTitle = (item) => {
  const fieldCategoryId = item?.fields?.find((field) =>
    getTemplateCategoryConfig(field?.category_id),
  )?.category_id;
  const sectionCategoryId = String(item?.section || "").startsWith(
    TEMPLATE_CATEGORY_SECTION_PREFIX,
  )
    ? String(item.section).slice(TEMPLATE_CATEGORY_SECTION_PREFIX.length)
    : "";
  const templateCategory = getTemplateCategoryConfig(
    fieldCategoryId || sectionCategoryId,
  );

  return (
    templateCategory?.label ||
    toTitleCase(item?.fields?.[0]?.block_label || item?.section || "N/A")
  );
};

export const normalizePreviewSections = ({
  sections = [],
  directItems = [],
  allFields = [],
}) => ({
  sections: sections.map((section) => ({
    ...section,
    fields: section.fields.map(normalizeTemplateCategoryPreviewField),
  })),
  directItems: directItems.map(normalizeTemplateCategoryPreviewField),
  allFields: allFields.map(normalizeTemplateCategoryPreviewField),
});

export const isHiddenPreviewField = (field) =>
  hiddenFields?.includes(field?.name) &&
  !getTemplateCategoryConfig(field?.category_id);

const deriveLabel = (field) => getPreviewFieldLabel(field);

const getDemoValue = (field, formField) => {
  const key = (field.name || "").toLowerCase();
  const lbl = deriveLabel(field).toLowerCase();
  const combined = key + " " + lbl;

  if (formField?.field_type === "selectGroup")
    return formField?.options?.[0] ?? null;

  if (/amount|cost|fee|price|total|subtotal|rate|value/.test(combined))
    return formField?.field_type === "number" ? 1500 : "$1,500.00";

  if (/hour|minute|hours|second/.test(combined)) return 1;

  if (/sq|area|footage|sqft|ftg/.test(combined))
    return formField?.field_type === "number" ? 1200 : "1,200";

  if (formField?.field_type === "number") return 1;

  if (/date|expir|issued|start|end/.test(combined)) return "01/15/2025";

  if (/phone|mobile|fax/.test(combined)) return "555-867-5309";

  if (/email/.test(combined)) return "sample@example.com";

  if (/zip|postal/.test(combined)) return "33101";

  if (/\bstate\b/.test(combined)) return "FL";

  if (/\bcity\b/.test(combined)) return "Miami";

  if (/county/.test(combined)) return "Miami-Dade";

  if (/address|street|addr/.test(combined)) return "123 Main Street";

  if (/unit|apt|suite|ste/.test(combined)) return "Suite 100";

  if (/owner|applicant|signer|qualifier|signee/.test(combined))
    return "John A. Smith";
  if (/contractor|company|business|corp|firm/.test(combined))
    return "ABC Construction LLC";
  if (/\bname\b/.test(combined)) return "Jane Doe";

  if (/license|permit|application|num|number/.test(combined))
    return "LIC-2025-001";

  if (/desc|note|comment|work|scope|detail/.test(combined))
    return "Sample description of work to be performed.";

  if (/project|package|lot|block/.test(combined)) return "Sample Project A";

  if (/folio|parcel/.test(combined)) return "01-2345-678-9000";

  return deriveLabel(field) || "Sample Text";
};

const handleMoneyChange = (event, setLocalValue) => {
  // Allow only numeric input and the decimal point during typing
  let { value } = event.target;
  value = parseDollarStrToNumber(value);

  setLocalValue(value);
};

const handleDateChange = (event, setLocalValue) => {
  let { value } = event.target;
  value = value.replace(/\D/g, ""); // Remove all non-digit characters

  // Format the value as mm/dd/yyyy
  let formattedValue = "";
  if (value.length > 4) {
    formattedValue = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(
      4,
    )}`;
  } else if (value.length > 2) {
    formattedValue = `${value.slice(0, 2)}/${value.slice(2)}`;
  } else {
    formattedValue = value;
  }

  // Validate and set the formatted value
  setLocalValue(formattedValue);
};

const handlePhoneInputChange = (event, setLocalValue) => {
  const { value } = event.target;
  let digitsOnly = value.replace(/\D/g, ""); // Remove non-numeric characters

  // If the number of digits is greater than 10, slice to 10 digits
  if (digitsOnly.length > 10) {
    digitsOnly = digitsOnly.slice(0, 10);
  }

  let formattedPhoneNumber = "";

  // Add dashes after 3rd and 6th digits
  for (let i = 0; i < digitsOnly.length; i++) {
    if (i === 3 || i === 6) {
      formattedPhoneNumber += "-";
    }
    formattedPhoneNumber += digitsOnly[i];
  }
  if (setLocalValue) {
    setLocalValue(formattedPhoneNumber);
  } else {
    return formattedPhoneNumber;
  }
};

const getFieldRow = (field) =>
  field?.row || field?.options?.find((opt) => opt?.row)?.row;

const getRowPrefix = (row) => row?.split("_")[0];

const getFieldOrder = (field) => field?.order ?? field?.options?.[0]?.order;

const getFieldKey = (field) =>
  String(
    isGenericTablePreviewField(field)
      ? (field?.field_instance_id ?? field?.id ?? field?.name ?? "")
      : (field?.field_id ?? field?.name ?? field?.order ?? ""),
  );

const getFieldSortId = (field) => `field:${getFieldKey(field)}`;

const getSectionSortId = (section) => `section:${String(section ?? "")}`;

const OPTION_SORT_ID_PREFIX = "option:";
const getOptionSortId = (option) => `${OPTION_SORT_ID_PREFIX}${option?.value}`;
const isOptionSortId = (id) =>
  typeof id === "string" && id.startsWith(OPTION_SORT_ID_PREFIX);
const getOptionValueFromSortId = (id) =>
  String(id).slice(OPTION_SORT_ID_PREFIX.length);

const dedupeRenderableFields = (renderableFields = []) => {
  const seen = new Set();

  return renderableFields.filter((field) => {
    const key = getFieldKey(field);

    if (!key || seen.has(key)) return false;

    seen.add(key);
    return true;
  });
};

const getRenderedFieldRawIds = (field) => {
  const ids = new Set();

  if (field?.field_id) ids.add(field.field_id);

  field?.options?.forEach((option) => {
    if (option?.value) ids.add(option.value);
  });

  return ids;
};

const buildRowUnits = (sortedFields) => {
  const units = [];
  const unitByPrefix = new Map();

  sortedFields.forEach((field) => {
    const row = getFieldRow(field);

    if (!row) {
      units.push({
        id: `unit:${getFieldKey(field)}`,
        type: "single",
        field,
        fields: [field],
      });
      return;
    }

    const prefix = getRowPrefix(row);
    const existingUnit = unitByPrefix.get(prefix);

    if (existingUnit) {
      existingUnit.fields.push(field);
      return;
    }

    const unit = {
      id: `row:${prefix}:${getFieldKey(field)}`,
      type: "row",
      field,
      fields: [field],
    };
    unitByPrefix.set(prefix, unit);
    units.push(unit);
  });

  return units;
};

export const LocalTextField = memo(
  ({ field, onCommit, onLabelCommit, demoVersion, locateFields }) => {
    const label = deriveLabel(field);
    const [localValue, setLocalValue] = useState(field.value || "");
    const [localLabel, setLocalLabel] = useState(label);
    const [isEditingLabel, setIsEditingLabel] = useState(false);
    const prevDemoRef = useRef(demoVersion);
    const labelEditorRef = useRef(null);
    const labelDraftRef = useRef(label);
    const isEditingLabelRef = useRef(false);
    const isMultiline = field.type === "multiline";
    const isValueEntryDisabled = locateFields && !isEditingLabel;

    useEffect(() => {
      if (demoVersion !== prevDemoRef.current || field.value !== localValue) {
        setLocalValue(field.value || "");
        prevDemoRef.current = demoVersion;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.value, demoVersion]);

    const isLong =
      (typeof localValue === "string" && localValue.length > 80) || isMultiline;

    const commitLabel = useCallback(() => {
      if (!isEditingLabelRef.current) return;

      const nextLabel = labelDraftRef.current;
      isEditingLabelRef.current = false;
      labelEditorRef.current?.blur();
      onLabelCommit?.(field, nextLabel);
      setLocalLabel(nextLabel.trim() || label);
      setIsEditingLabel(false);
    }, [field, label, onLabelCommit]);

    useEffect(() => {
      if (!isEditingLabel) return undefined;

      const handleClickAway = (event) => {
        if (labelEditorRef.current?.contains(event.target)) return;
        commitLabel();
      };

      document.addEventListener("pointerdown", handleClickAway, true);
      return () =>
        document.removeEventListener("pointerdown", handleClickAway, true);
    }, [commitLabel, isEditingLabel]);

    return (
      <TextField
        inputRef={labelEditorRef}
        inputProps={
          isEditingLabel
            ? { "data-preview-label-editor": "true" }
            : { readOnly: isValueEntryDisabled }
        }
        variant="filled"
        fullWidth
        margin="normal"
        label={isEditingLabel ? "Field label" : label}
        value={
          isEditingLabel
            ? localLabel
            : field.type === "dollar"
              ? (localValue ?? "")
              : localValue || ""
        }
        multiline={!isEditingLabel && isLong}
        type={field.type === "number" ? "number" : "text"}
        autoFocus={isEditingLabel}
        onDoubleClick={(event) => {
          if (!locateFields || isEditingLabel) return;

          event.preventDefault();
          event.stopPropagation();
          labelDraftRef.current = label;
          isEditingLabelRef.current = true;
          setLocalLabel(label);
          setIsEditingLabel(true);
        }}
        onChange={(e) => {
          if (isEditingLabel) {
            labelDraftRef.current = e.target.value;
            setLocalLabel(e.target.value);
          } else if (!locateFields) {
            setLocalValue(e.target.value);
          }
        }}
        onBlur={(event) => {
          if (isEditingLabel) {
            commitLabel();
            return;
          }

          if (locateFields) return;

          if (field.type === "dollar" || field.type === "total") {
            let { value } = event.target;
            const formattedValue = formatMoneyValue(value);
            onCommit(field?.name, formattedValue);
          } else {
            onCommit(field?.name, localValue);
          }
        }}
        onKeyDown={(e) => {
          if (isEditingLabel && e.key === "Enter") {
            e.preventDefault();
            e.currentTarget.blur();
            return;
          }

          if (
            field.value?.length > 310 &&
            e.key !== "Backspace" &&
            e.key !== "Delete" &&
            isMultiline
          )
            e.preventDefault();
        }}
        onKeyUp={(e) => {
          if (locateFields || isEditingLabel) return;

          if (field.type === "mobile") {
            handlePhoneInputChange(e, setLocalValue);
          } else if (field.type === "dollar") {
            handleMoneyChange(e, setLocalValue);
          } else if (field.type === "date" || field.type === "todayDate") {
            handleDateChange(e, setLocalValue);
          }
        }}
        rows={4}
      />
    );
  },
);

const SortableRadioOption = memo(({ option, enableSorting }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: getOptionSortId(option),
      disabled: !enableSorting,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    display: "flex",
    alignItems: "center",
  };

  return (
    <Box ref={setNodeRef} style={style}>
      {enableSorting && (
        <DragIndicatorIcon
          {...attributes}
          {...listeners}
          sx={{
            fontSize: "1rem",
            fill: "#ababab",
            cursor: "grab",
            mr: 1,
          }}
        />
      )}
      <FormControlLabel
        control={<Radio />}
        label={option.label}
        value={option.value}
      />
    </Box>
  );
});

const FieldContent = memo(
  ({
    field,
    onChange,
    onLabelCommit,
    demoVersion,
    docFields,
    allFormField,
    fields,
    locateFields,
    enableSorting,
  }) => {
    const label = deriveLabel(field);

    const { value, name, multiple } = field;

    const currentSession = useSelector(
      (state) => state.sessions.currentSession,
    );
    const currentUser = useSelector((state) => state[currentSession]?.user);
    const designProList = currentUser?.design_professionals;

    // Memoize dependency values based on subscriber field keys.
    // This ensures the effect only runs when relevant form values change.
    const dependency = useMemo(() => {
      const subscribers = field?.formulaFields?.map((f) => f?.field?.name);

      if (!subscribers?.length) {
        return [];
      }

      const allSumFields = allFormField?.filter((field) =>
        subscribers?.includes(field?.name),
      );

      // This creates a dependency list that tracks only specific fields
      return allSumFields.map((field) => field?.value);
    }, [field?.formulaFields, allFormField]);

    useEffect(() => {
      if (!field?.formulaFields?.length) return;

      const firstVal = allFormField?.find(
        (f) => f?.name === field?.formulaFields?.[0]?.field?.name,
      )?.value;
      let result = parseFloat(parseDollarStrToNumber(firstVal) ?? 0);

      for (let i = 1; i < field?.formulaFields?.length; i++) {
        const formField = allFormField?.find(
          (f) => f?.name === field?.formulaFields?.[i]?.field?.name,
        );

        const val = parseFloat(parseDollarStrToNumber(formField?.value) ?? 0);

        switch (field?.formulaFields?.[i]?.operator?.value) {
          case "+":
            result += val;
            break;
          case "-":
            result -= val;
            break;
          case "*":
            result *= val;
            break;
          case "/":
            result = val !== 0 ? result / val : result;
            break;
          case "=":
            result = val;
            break;
          case "%":
            result = result * ((field?.formulaFields?.[i]?.value ?? val) / 100);
            break;
          default:
            break;
        }
      }

      if (result !== undefined) {
        // Update the target field with computed value
        // This keeps the field in sync with dependent form values
        setTimeout(() => {
          onChange(name, formatMoneyValue(result === 0 ? "0" : result));
        }, 50);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependency);

    if (["PDFRectangle", "PDFLine"].includes(field.type)) return null;

    return (
      <Box flex={1} minWidth={0}>
        {field.type === "designProAuto" ? (
          <Box sx={{ mt: 2, mb: 1 }}>
            <Autocomplete
              freeSolo
              sx={{ width: "100%" }}
              options={designProList}
              value={
                designProList?.find((option) => option.companyName === value) ||
                value ||
                ""
              }
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.companyName
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  onChange={(e) => {
                    onChange(name, e.target.value);
                  }}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          {value && value !== "" && (
                            <IconButton
                              onClick={() => {
                                const mappingList = [
                                  {
                                    formikNames: [
                                      "engineer",
                                      "engineer_name",
                                      "engineerName",
                                    ],
                                    keys: [
                                      "engineerPhone",
                                      "engineer_phone",
                                      "engineerEmail",
                                      "engineer_email",
                                      "engineerAddress",
                                    ],
                                  },
                                  {
                                    formikNames: [
                                      "architect",
                                      "architectName",
                                      "architect_name",
                                    ],
                                    keys: [
                                      "architectPhone",
                                      "architect_phone",
                                      "architectEmail",
                                      "architect_email",
                                      "architectAddress",
                                    ],
                                  },
                                ];

                                // Loop through mappingList and clear the matching fields
                                mappingList.forEach((mapping) => {
                                  if (
                                    mapping.formikNames.includes(field.name)
                                  ) {
                                    mapping.keys.forEach((field) => {
                                      onChange(field, "");
                                    });
                                  }
                                });

                                // Finally, clear the main field
                                onChange(name, "");
                              }}
                              size="small"
                              sx={{
                                marginRight: -4,
                                marginTop: -2,
                              }}
                            >
                              <ClearIcon sx={{ fontSize: "20px" }} />
                            </IconButton>
                          )}
                        </Box>
                      ),
                    },
                  }}
                />
              )}
              onChange={(event, selectedOption) => {
                if (selectedOption) {
                  const mappingList = [
                    {
                      formikNames: [
                        "engineer",
                        "engineer_name",
                        "engineerName",
                      ],
                      keys: [
                        {
                          engineerPhone: "phone",
                          engineerEmail: "email",
                          engineer_email: "email",
                          engineer_phone: "phone",
                          engineerAddress: "address",
                        },
                      ],
                    },
                    {
                      formikNames: [
                        "architect",
                        "architectName",
                        "architect_name",
                      ],
                      keys: [
                        {
                          architectPhone: "phone",
                          architect_phone: "phone",
                          architectEmail: "email",
                          architect_email: "email",
                          architectAddress: "address",
                        },
                      ],
                    },
                  ];

                  // Set the selected company's name
                  onChange(name, selectedOption.companyName);

                  // Update only for the matching formikNames in the list
                  mappingList.forEach((mapping) => {
                    if (mapping.formikNames.includes(field.name)) {
                      mapping.keys.forEach((keyObj) => {
                        Object.entries(keyObj).forEach(([key, value]) => {
                          onChange(key, selectedOption[value]);
                        });
                      });
                    }
                  });
                }
              }}
              slotProps={{
                listbox: {
                  style: {
                    maxHeight: "150px",
                    overflowY: "auto",
                  },
                },
              }}
            />
          </Box>
        ) : [
            "text",
            "mobile",
            "email",
            "dollar",
            "number",
            "date",
            "todayDate",
            "addressGroup",
            "mobileGroup",
            "multiline",
          ].includes(field.type) ? (
          <LocalTextField
            field={field}
            onCommit={onChange}
            onLabelCommit={onLabelCommit}
            demoVersion={demoVersion}
            locateFields={locateFields}
          />
        ) : field.type === "checkboxGroup" ? (
          (() => {
            const visibleOptions = field?.options?.filter(
              (option) =>
                isVisible(option, allFormField, docFields) && !field.compact,
            );
            return (
              visibleOptions?.length > 0 && (
                <>
                  {field?.title && (
                    <Typography
                      sx={{ width: "100%", mt: 2, fontSize: "16px" }}
                      variant="h6"
                    >
                      {field.title}
                    </Typography>
                  )}
                  <FormControl component="fieldset" sx={{ width: "100%" }}>
                    {(() => {
                      const optionValues = (visibleOptions || []).map(
                        (o) => o.value,
                      );

                      const docOptionValue = docFields
                        ?.filter((f) => optionValues.includes(f?.field_id))
                        .find((f) => f.value);

                      const safeVal =
                        docOptionValue?.field_id ??
                        (isPlainObject(value) && value?.value
                          ? value?.value
                          : value);

                      return (
                        <RadioGroup
                          row
                          value={safeVal || ""}
                          onChange={(e) => {
                            const value = visibleOptions?.find(
                              (o) => o.value === e.target.value,
                            );
                            onChange(name, value, optionValues);
                          }}
                        >
                          <SortableContext
                            items={visibleOptions.map((option) =>
                              getOptionSortId(option),
                            )}
                            strategy={horizontalListSortingStrategy}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: { xs: "column", md: "row" },
                                width: "100%",
                              }}
                            >
                              {visibleOptions?.map((option) => (
                                <SortableRadioOption
                                  key={option.value}
                                  option={option}
                                  enableSorting={enableSorting}
                                />
                              ))}
                            </Box>
                          </SortableContext>
                        </RadioGroup>
                      );
                    })()}
                  </FormControl>
                </>
              )
            );
          })()
        ) : field.type === "selectGroup" ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              width: "100%",
              minWidth: 0,
            }}
          >
            <FormControl
              fullWidth
              margin="normal"
              variant="filled"
              sx={{ minWidth: 0 }}
            >
              <InputLabel>{label}</InputLabel>
              {(() => {
                const optionValues = (field.options || []).map((o) => o.value);
                const selectedIds = (docFields || [])
                  .filter((f) => optionValues.includes(f?.field_id) && f.value)
                  .map((f) => f.field_id);

                const safeVal = multiple
                  ? selectedIds
                  : (selectedIds[0] ??
                    (optionValues.includes(value?.value) ? value.value : ""));

                return (
                  <Select
                    multiple={multiple}
                    value={safeVal}
                    onChange={(event) => {
                      if (multiple) {
                        onChange(name, event.target.value, optionValues, true);
                      } else {
                        const selectedOption = field.options?.find(
                          (o) => o.value === event.target.value,
                        );
                        onChange(name, selectedOption, optionValues);
                      }
                    }}
                    label={label}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          marginTop: "1.5px",
                          maxHeight: 200,
                          width: "auto",
                          maxWidth: "100%",
                        },
                      },
                      anchorOrigin: { vertical: "bottom", horizontal: "left" },
                      transformOrigin: { vertical: "top", horizontal: "left" },
                    }}
                  >
                    {field.options?.map((option, i) => (
                      <MenuItem key={option.value + i} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                );
              })()}
            </FormControl>
          </Box>
        ) : field.type === "privateProvider" ? (
          <RenderPrivateProvider
            full_title={field?.field_label}
            privateProvider={field.value}
            onChange={onChange}
            fields={fields}
          />
        ) : field.type === "full_title" ? (
          <Typography mt={2} mb={1} align="center" variant="h6">
            {field?.field_label}
          </Typography>
        ) : field.type === "full_subTitle" ? (
          <Typography mt={1.5} mb={0} align="center" variant="subtitle1">
            {field?.field_label}
          </Typography>
        ) : field.type === "project_contact_details" ? (
          <RenderProjectContact
            full_title={field?.field_label}
            projectContact={field.value}
            onChange={onChange}
            fields={fields}
          />
        ) : field.type === "designProfessional" ? (
          <RenderDesignPro
            full_title={field?.field_label}
            designPro={field.value}
            onChange={onChange}
            fields={fields}
          />
        ) : field.type === "feeSimple" ? (
          <RenderFeeSimple
            full_title={field?.field_label}
            feeSimple={field.value}
            onChange={onChange}
            fields={fields}
          />
        ) : field.type === "bonding" ? (
          <RenderBonding
            full_title={field?.field_label}
            bonding={field.value}
            onChange={onChange}
            fields={fields}
          />
        ) : field.type === "mortgage" ? (
          <RenderMortgage
            full_title={field?.field_label}
            mortgage={field.value}
            onChange={onChange}
            fields={fields}
          />
        ) : field.type === "termite" ? (
          <RenderTermite
            full_title={field?.field_label}
            termite={field.value}
            onChange={onChange}
            fields={fields}
          />
        ) : field.type === "total" ? (
          field?.isEditable ? (
            <LocalTextField
              field={field}
              onCommit={onChange}
              onLabelCommit={onLabelCommit}
              demoVersion={demoVersion}
              locateFields={locateFields}
            />
          ) : (
            <Stack>
              <Typography
                mt={1.5}
                mb={0}
                align="center"
                color="primary"
                fontSize={16}
                fontWeight={700}
              >
                {field?.field_label}
              </Typography>
              <Typography mt={0.5} mb={0} align="center" fontWeight={700}>
                {field?.value || "$0.00"}
              </Typography>
            </Stack>
          )
        ) : field.type === "percentageCheckbox" ? (
          <Box sx={{ display: "flex", justifyContent: "end" }}>
            <FormControlLabel
              control={
                <Checkbox
                  name={field?.name}
                  checked={value}
                  value={value}
                  onChange={(e) => {
                    const currentValue = e.target.checked;
                    onChange(name, currentValue);

                    const totalCost = allFormField?.find((f) =>
                      totalFields?.includes(f?.name),
                    );
                    const totalCostNum = parseFloat(
                      parseDollarStrToNumber(totalCost?.value) ?? 0,
                    );

                    allFormField
                      ?.filter(
                        (f) =>
                          ["dollar", "total"].includes(f.type) &&
                          !totalFields?.includes(f?.name),
                      )
                      .forEach((f) => {
                        const fieldNum = parseFloat(
                          parseDollarStrToNumber(f?.value) ?? 0,
                        );
                        const val = currentValue
                          ? formatPercentageValue(
                              (fieldNum / totalCostNum) * 100,
                            )
                          : formatMoneyValue((fieldNum * totalCostNum) / 100);
                        onChange(f?.name, val);
                      });
                  }}
                  slotProps={{
                    input: { "aria-label": "controlled" },
                  }}
                />
              }
              label={label}
              slotProps={{
                typography: { component: "span", variant: "body2" },
              }}
            />
          </Box>
        ) : null}
      </Box>
    );
  },
);

const FieldDragHandle = ({ fieldType, attributes, listeners }) => (
  <DragIndicatorIcon
    {...attributes}
    {...listeners}
    sx={{
      fontSize: "1.2rem",
      fill: "#ababab",
      mt: [
        "privateProvider",
        "project_contact_details",
        "designProfessional",
        "feeSimple",
        "bonding",
        "mortgage",
        "termite",
      ]?.includes(fieldType)
        ? 5.5
        : 0.5,
      cursor: "grab",
    }}
  />
);

const StandaloneField = memo(
  ({
    unitId,
    field,
    onChange,
    onLabelCommit,
    demoVersion,
    docFields,
    allFormField,
    fields,
    enableSorting,
    locateFields,
    isLocated,
    onLocateField,
    isRowSelected,
    onToggleRowField,
  }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id: unitId, disabled: !enableSorting });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    const visibleOptions = field?.options?.filter(
      (option) => isVisible(option, allFormField, docFields) && !field.compact,
    );

    if (["PDFRectangle", "PDFLine"].includes(field.type)) return null;

    return (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "default",
          "& input, & textarea": {
            cursor: locateFields ? "default" : undefined,
          },
          borderRadius: 1,
          outline: isLocated || isRowSelected ? "2px solid" : "none",
          outlineColor: isRowSelected ? "primary.main" : "secondary.main",
          outlineOffset: 2,
          backgroundColor: isRowSelected
            ? "rgba(25, 118, 210, 0.08)"
            : "transparent",
        }}
        ref={setNodeRef}
        data-preview-field-key={getFieldKey(field)}
        style={style}
        onMouseDownCapture={(event) => {
          if (event.target.closest?.("[data-preview-label-editor]")) return;
          if (!locateFields && !event.ctrlKey && !event.metaKey) return;
          event.preventDefault();
          event.stopPropagation();
        }}
        onClickCapture={(event) => {
          if (event.target.closest?.("[data-preview-label-editor]")) return;
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            event.stopPropagation();
            onToggleRowField?.(field);
            return;
          }
          if (!locateFields) return;
          event.preventDefault();
          event.stopPropagation();
          onLocateField?.(field);
        }}
      >
        {field.type &&
          enableSorting &&
          (!field?.options?.length || visibleOptions?.length > 0) && (
            <FieldDragHandle
              fieldType={field.type}
              attributes={attributes}
              listeners={listeners}
            />
          )}
        <FieldContent
          field={field}
          onChange={onChange}
          onLabelCommit={onLabelCommit}
          demoVersion={demoVersion}
          docFields={docFields}
          allFormField={allFormField}
          fields={fields}
          locateFields={locateFields}
          enableSorting={enableSorting}
        />
      </Box>
    );
  },
);

const RowField = memo(
  ({
    field,
    onChange,
    onLabelCommit,
    demoVersion,
    docFields,
    allFormField,
    fields,
    enableSorting,
    locateFields,
    isLocated,
    onLocateField,
    isRowSelected,
    onToggleRowField,
  }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id: getFieldSortId(field), disabled: !enableSorting });
    const style = { transform: CSS.Transform.toString(transform), transition };

    if (["PDFRectangle", "PDFLine"].includes(field.type)) return null;

    const visibleOptions = field?.options?.filter(
      (option) => isVisible(option, allFormField, docFields) && !field.compact,
    );

    return (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "default",
          "& input, & textarea": {
            cursor: locateFields ? "default" : undefined,
          },
          borderRadius: 1,
          outline: isLocated || isRowSelected ? "2px solid" : "none",
          outlineColor: isRowSelected ? "primary.main" : "secondary.main",
          outlineOffset: 2,
          backgroundColor: isRowSelected
            ? "rgba(25, 118, 210, 0.08)"
            : "transparent",
        }}
        ref={setNodeRef}
        data-preview-field-key={getFieldKey(field)}
        style={style}
        onMouseDownCapture={(event) => {
          if (event.target.closest?.("[data-preview-label-editor]")) return;
          if (!locateFields && !event.ctrlKey && !event.metaKey) return;
          event.preventDefault();
          event.stopPropagation();
        }}
        onClickCapture={(event) => {
          if (event.target.closest?.("[data-preview-label-editor]")) return;
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            event.stopPropagation();
            onToggleRowField?.(field);
            return;
          }
          if (!locateFields) return;
          event.preventDefault();
          event.stopPropagation();
          onLocateField?.(field);
        }}
      >
        {enableSorting &&
          (!field?.options?.length || visibleOptions?.length > 0) && (
            <DragIndicatorIcon
              {...attributes}
              {...listeners}
              sx={{
                fontSize: "1rem",
                fill: "#ababab",
                cursor: "grab",
                mt: [
                  "privateProvider",
                  "project_contact_details",
                  "designProfessional",
                  "feeSimple",
                  "bonding",
                  "mortgage",
                  "termite",
                ]?.includes(field?.type)
                  ? 5.5
                  : 0.5,
              }}
            />
          )}
        <FieldContent
          field={field}
          onChange={onChange}
          onLabelCommit={onLabelCommit}
          demoVersion={demoVersion}
          docFields={docFields}
          allFormField={allFormField}
          fields={fields}
          locateFields={locateFields}
          enableSorting={enableSorting}
        />
      </Box>
    );
  },
);
const isVisible = (field, fields, docFields) => {
  if (!field.parents) return true;

  return field.parents?.every((parent) => {
    const parentField = fields?.find((f) => f?.name === parent?.name);

    const optionValues = (parentField.options || []).map((o) => o.value);

    const docOptionValue = docFields
      ?.filter((f) => optionValues.includes(f?.field_id))
      .find((f) => f.value);

    const formValue = docOptionValue?.field_id ?? parentField?.value?.value;
    const matchedFilter = filter_number_input_values?.find(
      (filter) => filter.parentName === parent?.name,
    );

    if (Array.isArray(parent?.value)) {
      return parent.value.find((f) => f.value === formValue);
    } else if (matchedFilter) {
      // If it's based on "num_units", check logic to show fields based on selection
      const selectedUnitIndex =
        unitNumMapping[formValue?.replace(matchedFilter.formValue, "")];
      // Extract numeric value from "unit_one", "unit_two"
      const unitIndex =
        unitNumMapping[parent?.value?.replace(matchedFilter.formValue, "")]; // Extract numeric value for build_ fields

      // Show fields that correspond to <= the selected number of units
      return unitIndex <= selectedUnitIndex;
    } else {
      return formValue === parent.value;
    }
  });
};

const RowContainer = ({
  children,
  rowId,
  fieldIds,
  enableSorting,
  childFields,
  allFormField,
  docFields,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: rowId, disabled: !enableSorting });

  const style = { transform: CSS.Transform.toString(transform), transition };

  const visibleChildFields = childFields.filter((f) => {
    const visibleOptions = f?.options?.filter(
      (option) => isVisible(option, allFormField, docFields) && !f.compact,
    );

    return !f?.options?.length || visibleOptions?.length > 0;
  });

  return (
    <Box
      ref={setNodeRef}
      style={enableSorting ? style : undefined}
      sx={{
        display: "flex",
        width: "100%",
        minWidth: 0,
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
      }}
    >
      {enableSorting && visibleChildFields?.length > 0 && (
        <DragIndicatorIcon
          {...attributes}
          {...listeners}
          sx={{ fontSize: "1.2rem", fill: "#ababab", mt: 0.5, cursor: "grab" }}
        />
      )}
      <SortableContext
        items={fieldIds ?? []}
        strategy={horizontalListSortingStrategy}
      >
        {children}
      </SortableContext>
    </Box>
  );
};

const RenderUnit = ({
  unit,
  onChange,
  onLabelCommit,
  demoVersion,
  docFields,
  allFormField,
  enableSorting,
  sectionFields,
  locateFields,
  locatedField,
  onLocateField,
  rowSelectedFieldKeys,
  onToggleRowField,
}) => {
  if (unit.type === "row") {
    const fieldIds = unit.fields.map((f) => getFieldSortId(f));
    return (
      <RowContainer
        rowId={unit.id}
        fieldIds={fieldIds}
        enableSorting={enableSorting}
        childFields={unit.fields}
        docFields={docFields}
        allFormField={allFormField}
      >
        {unit.fields.map((fieldInRow) => (
          <RowField
            key={fieldInRow.name}
            field={fieldInRow}
            onChange={onChange}
            onLabelCommit={onLabelCommit}
            demoVersion={demoVersion}
            docFields={docFields}
            allFormField={allFormField}
            fields={sectionFields}
            enableSorting={enableSorting}
            locateFields={locateFields}
            isLocated={getRenderedFieldRawIds(fieldInRow).has(
              locatedField?.field_id,
            )}
            onLocateField={onLocateField}
            isRowSelected={rowSelectedFieldKeys.has(getFieldKey(fieldInRow))}
            onToggleRowField={onToggleRowField}
          />
        ))}
      </RowContainer>
    );
  }

  return (
    <StandaloneField
      unitId={unit.id}
      field={unit.fields[0]}
      onChange={onChange}
      onLabelCommit={onLabelCommit}
      demoVersion={demoVersion}
      docFields={docFields}
      allFormField={allFormField}
      fields={sectionFields}
      enableSorting={enableSorting}
      locateFields={locateFields}
      isLocated={getRenderedFieldRawIds(unit.fields[0]).has(
        locatedField?.field_id,
      )}
      onLocateField={onLocateField}
      isRowSelected={rowSelectedFieldKeys.has(getFieldKey(unit.fields[0]))}
      onToggleRowField={onToggleRowField}
    />
  );
};

const SectionCard = memo(
  ({
    item,
    onFieldChange,
    onFieldLabelChange,
    demoVersion,
    docFields,
    allFormField,
    handleDragEnd,
    enableSorting,
    sectionDragActive,
    locateFields,
    locatedField,
    onLocateField,
    rowSelectedFieldKeys,
    onToggleRowField,
  }) => {
    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      }),
    );
    const {
      attributes: sectionAttributes,
      listeners: sectionListeners,
      setNodeRef: setSectionNodeRef,
      transform: sectionTransform,
      transition: sectionTransition,
      isDragging: isSectionDragging,
    } = useSortable({
      id: getSectionSortId(item.section),
      disabled: !enableSorting,
    });

    const sectionStyle = {
      transform: CSS.Transform.toString(sectionTransform),
      transition: sectionTransition,
      opacity: isSectionDragging ? 0 : 1,
    };

    const items = item.fields?.filter((field) => !isHiddenPreviewField(field));

    const sortedItems = dedupeRenderableFields(items).sort(
      (a, b) => getFieldOrder(a) - getFieldOrder(b),
    );
    const rowUnits = buildRowUnits(sortedItems);
    const sortableIds = rowUnits.map((u) => u.id);
    return (
      <Box
        ref={setSectionNodeRef}
        style={enableSorting ? sectionStyle : undefined}
        sx={{
          backgroundColor: "#fff",
          border: "2px solid #d3d3d3",
          borderRadius: "15px",
          px: 2,
          pb: 1,
          boxShadow:
            "0px 4px 6px rgba(0, 0, 0, 0.4), 0px 1px 3px rgba(0, 0, 0, 0.08)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          {enableSorting && (
            <DragIndicatorIcon
              {...sectionAttributes}
              {...sectionListeners}
              sx={{
                position: "absolute",
                left: 0,
                fontSize: "1.4rem",
                fill: "#ababab",
                cursor: "grab",
              }}
            />
          )}
          <Typography variant="h5" align="center" mb={2} mt={1} color="primary">
            {getPreviewSectionTitle(item)}
          </Typography>
        </Box>
        {!sectionDragActive && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortableIds ?? []}
              strategy={verticalListSortingStrategy}
            >
              <Grid container spacing={1}>
                {rowUnits.map((unit) => {
                  const visibleFields = unit.fields.filter((f) =>
                    isVisible(f, allFormField, docFields),
                  );
                  if (visibleFields.length === 0) return null;

                  const isCompact =
                    unit.field?.type === "addressGroup" ||
                    unit.field?.type === "mobileGroup";

                  return (
                    <Grid
                      key={`unit-${unit.id}`}
                      size={{
                        xs: 12,
                        sm: 12,
                        md: isCompact ? 4 : 12,
                      }}
                      px={1}
                    >
                      <RenderUnit
                        unit={{ ...unit, fields: visibleFields }}
                        onChange={onFieldChange}
                        onLabelCommit={onFieldLabelChange}
                        demoVersion={demoVersion}
                        docFields={docFields}
                        allFormField={allFormField}
                        enableSorting={enableSorting}
                        sectionFields={item.fields}
                        locateFields={locateFields}
                        locatedField={locatedField}
                        onLocateField={onLocateField}
                        rowSelectedFieldKeys={rowSelectedFieldKeys}
                        onToggleRowField={onToggleRowField}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </SortableContext>
          </DndContext>
        )}
      </Box>
    );
  },
);

const DirectFieldCard = memo(
  ({
    fields,
    onFieldChange,
    onFieldLabelChange,
    demoVersion,
    docFields,
    allFormField,
    handleDragEnd,
    enableSorting,
    locateFields,
    locatedField,
    onLocateField,
    rowSelectedFieldKeys,
    onToggleRowField,
  }) => {
    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      }),
    );

    if (!fields?.length) return null;

    const items = fields?.filter((field) => !isHiddenPreviewField(field));

    const sortedItems = dedupeRenderableFields(items).sort(
      (a, b) => getFieldOrder(a) - getFieldOrder(b),
    );
    const rowUnits = buildRowUnits(sortedItems);
    const sortableIds = rowUnits.map((u) => u.id);

    return (
      <Box
        sx={{
          backgroundColor: "#fff",
          border: "2px solid #d3d3d3",
          borderRadius: "15px",
          px: 2,
          pb: 1,
          boxShadow:
            "0px 4px 6px rgba(0, 0, 0, 0.4), 0px 1px 3px rgba(0, 0, 0, 0.08)",
        }}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortableIds ?? []}
            strategy={verticalListSortingStrategy}
          >
            <Grid container spacing={1}>
              {rowUnits.map((unit) => {
                const visibleFields = unit.fields.filter((f) =>
                  isVisible(f, allFormField, docFields),
                );
                if (visibleFields.length === 0) return null;

                const isCompact =
                  unit.field?.type === "addressGroup" ||
                  unit.field?.type === "mobileGroup";

                return (
                  <Grid
                    key={`unit-${unit.id}`}
                    size={{
                      xs: 12,
                      sm: 12,
                      md: isCompact ? 4 : 12,
                    }}
                    px={1}
                  >
                    <RenderUnit
                      unit={{ ...unit, fields: visibleFields }}
                      onChange={onFieldChange}
                      onLabelCommit={onFieldLabelChange}
                      demoVersion={demoVersion}
                      docFields={docFields}
                      allFormField={allFormField}
                      enableSorting={enableSorting}
                      sectionFields={fields}
                      locateFields={locateFields}
                      locatedField={locatedField}
                      onLocateField={onLocateField}
                      rowSelectedFieldKeys={rowSelectedFieldKeys}
                      onToggleRowField={onToggleRowField}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </SortableContext>
        </DndContext>
      </Box>
    );
  },
);
const SKIP_TYPES = new Set(["PDFRectangle", "PDFLine"]);
const CANVAS_AREA_FIELD_ID = "801fc1f4-945b-4ac7-8ca7-42f307178039";
const isCanvasAreaField = (field) => field?.field_id === CANVAS_AREA_FIELD_ID;

const PreviewForm = ({
  currentProcessingFile,
  setCurrentProcessingFile,
  setDocCurrentProcessingFile,
  fillDemoRef,
  clearRef,
  fields,
  enableSorting,
  locateFields,
  locatedField,
  onLocateField,
  onHoverField,
  rowSelectedFields,
  onToggleRowField,
  scrollToFieldRequest,
}) => {
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState([]);
  const [directItems, setDirectItems] = useState([]);
  const [allFormField, setAllFormField] = useState([]);
  const [demoVersion, setDemoVersion] = useState(0);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const scrollContainerRef = useRef(null);
  const rowSelectedFieldKeys = useMemo(
    () => new Set(rowSelectedFields.map((field) => getFieldKey(field))),
    [rowSelectedFields],
  );
  const sectionSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    const fieldArray = (
      currentProcessingFile?.document?.tempFields?.formFields ?? []
    )
      .filter(isRightPreviewFormField)
      .filter((field) => !isCanvasAreaField(field))
      .filter((field) => !isGenericTablePreviewField(field));

    const { sections, directItems, allFields } = normalizePreviewSections(
      getSections(fieldArray, fields),
    );

    setSections(sections);
    setDirectItems(directItems);
    setAllFormField(allFields);
    setLoading(false);
  }, [currentProcessingFile?.document?.tempFields?.formFields, fields]);

  useEffect(() => {
    if (!locateFields || !scrollToFieldRequest?.fieldId) return;

    const matchingField = allFormField.find((field) =>
      getRenderedFieldRawIds(field).has(scrollToFieldRequest.fieldId),
    );
    const fieldKey = getFieldKey(matchingField);
    if (!fieldKey) return;

    const frameId = requestAnimationFrame(() => {
      scrollPreviewFieldIntoView(scrollContainerRef.current, fieldKey);
    });

    return () => cancelAnimationFrame(frameId);
  }, [allFormField, locateFields, scrollToFieldRequest]);

  const mapFormFields = useCallback(
    (transform) =>
      setCurrentProcessingFile((prev) => ({
        ...prev,
        document: {
          ...prev.document,
          tempFields: {
            ...prev.document.tempFields,
            formFields: prev.document.tempFields.formFields?.map(transform),
          },
        },
      })),
    [setCurrentProcessingFile],
  );

  const handleFieldChange = useCallback(
    (name, newValue, optionValues, isMultiple) => {
      const form_field_id = allFormField?.find(
        (field) => field?.name === name,
      )?.field_id;

      return mapFormFields((f) => {
        if (f.field_id === form_field_id) return { ...f, value: newValue };

        if (isMultiple && optionValues?.includes(f.field_id)) {
          return { ...f, value: newValue.includes(f.field_id) };
        }
        if (newValue?.value && optionValues?.includes(f.field_id))
          return { ...f, value: f.field_id === newValue.value };
        return f;
      });
    },
    [mapFormFields, allFormField],
  );

  const handleGenericTableCellChange = useCallback(
    (changedField, newValue) => {
      const changedInstanceId = changedField?.field_instance_id;
      const changedName = changedField?.name;

      return mapFormFields((field) => {
        const isSameField = changedInstanceId
          ? field?.field_instance_id === changedInstanceId
          : field?.name === changedName;

        return isSameField ? { ...field, value: newValue } : field;
      });
    },
    [mapFormFields],
  );

  const handleFieldLabelChange = useCallback(
    (field, nextLabel) => {
      const updateLabel = (prev) => ({
        ...prev,
        document: {
          ...prev.document,
          tempFields: {
            ...prev.document.tempFields,
            formFields: updatePreviewFieldInstanceLabel(
              prev.document.tempFields.formFields,
              field,
              nextLabel,
            ),
          },
        },
      });

      setCurrentProcessingFile(updateLabel);
      setDocCurrentProcessingFile(updateLabel);
    },
    [setCurrentProcessingFile, setDocCurrentProcessingFile],
  );

  const handleFillDemo = useCallback(() => {
    mapFormFields((f) => {
      if (isGenericTablePreviewField(f)) {
        const genericFieldType =
          f?.generic_table?.field_type || f?.field_type || "text";
        const demoField = {
          ...f,
          name:
            f?.generic_table?.column_name ||
            f?.generic_table?.table_label ||
            f?.name,
          label: [
            f?.generic_table?.table_label,
            f?.generic_table?.row_name,
            f?.generic_table?.column_name,
          ]
            .filter(Boolean)
            .join(" "),
        };

        return {
          ...f,
          value: getDemoValue(demoField, {
            field_type: genericFieldType,
          }),
        };
      }

      const formField = allFormField?.find(
        (field) => field?.field_id === f.field_id,
      );
      const childField = allFormField?.find((field) =>
        field?.options?.some((o) => o.value === f.field_id),
      );

      if (SKIP_TYPES.has(f.type) || !(childField || formField)) return f;

      if (
        childField?.field_type === "selectGroup" ||
        childField?.field_type === "checkboxGroup"
      ) {
        const isFirstOpt = childField.options?.[0]?.value === f.field_id;
        return { ...f, value: isFirstOpt };
      }

      return {
        ...f,
        value: getDemoValue(
          { ...f, label: f?.field_label },
          formField ?? childField,
        ),
      };
    });
    setDemoVersion((v) => v + 1);
  }, [mapFormFields, allFormField]);

  const handleClear = useCallback(() => {
    mapFormFields((f) => {
      if (SKIP_TYPES.has(f.type)) return f;
      return {
        ...f,
        value:
          f.type === "PDFCheckBox" || f.type === "PDFRadioGroup" ? false : "",
      };
    });
    setDemoVersion((v) => v + 1);
  }, [mapFormFields]);

  const withReorderedFields = (prev, reorderedFields) => ({
    ...prev,
    document: {
      ...prev.document,
      tempFields: { ...prev.document.tempFields, formFields: reorderedFields },
    },
  });

  const handleOptionDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const activeValue = getOptionValueFromSortId(active.id);
      const overValue = getOptionValueFromSortId(over.id);

      const parentField = allFormField?.find((field) =>
        field?.options?.some((o) => o.value === activeValue),
      );
      if (!parentField) return;

      const optionValues = (parentField.options || []).map((o) => o.value);
      const fromIdx = optionValues.indexOf(activeValue);
      const toIdx = optionValues.indexOf(overValue);
      if (fromIdx === -1 || toIdx === -1) return;

      const reorderedValues = arrayMove(optionValues, fromIdx, toIdx);

      const reorder = (prev) => {
        const rawFields = prev.document.tempFields.formFields ?? [];

        const matchingRawFields = reorderedValues
          .map((optionValue) =>
            rawFields.find((f) => f.field_id === optionValue),
          )
          .filter(Boolean);

        if (matchingRawFields.length !== reorderedValues.length) return prev;

        const hasValidOrders = matchingRawFields.every((f) =>
          Number.isFinite(Number(f.order)),
        );
        const existingOrders = hasValidOrders
          ? matchingRawFields.map((f) => Number(f.order)).sort((a, b) => a - b)
          : matchingRawFields.map((_, i) => i);

        const orderByFieldId = new Map(
          matchingRawFields.map((f, i) => [f.field_id, existingOrders[i]]),
        );

        const reorderedFields = rawFields.map((f) =>
          orderByFieldId.has(f.field_id)
            ? { ...f, order: orderByFieldId.get(f.field_id) }
            : f,
        );

        return withReorderedFields(prev, reorderedFields);
      };

      setDocCurrentProcessingFile(reorder);
      setCurrentProcessingFile(reorder);
    },
    [allFormField, setCurrentProcessingFile, setDocCurrentProcessingFile],
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      if (isOptionSortId(active.id) || isOptionSortId(over.id)) {
        handleOptionDragEnd(event);
        return;
      }

      const sortFields = (prev) => {
        const rawFields = prev.document.tempFields.formFields ?? [];
        const { allFields } = getSections(rawFields, fields);
        const sortedRenderableFields = dedupeRenderableFields(allFields).sort(
          (a, b) => getFieldOrder(a) - getFieldOrder(b),
        );
        const units = buildRowUnits(sortedRenderableFields);

        const isFieldId = (id) =>
          typeof id === "string" && id.startsWith("field:");
        const isUnitId = (id) =>
          typeof id === "string" &&
          (id.startsWith("row:") || id.startsWith("unit:"));

        const getOwnerUnitId = (id) => {
          if (isUnitId(id)) return id;

          if (!isFieldId(id)) return null;

          const ownerUnit = units.find((unit) =>
            unit.fields.some((field) => getFieldSortId(field) === id),
          );

          return ownerUnit?.id ?? null;
        };

        const rawBlockByRenderedFieldId = new Map(
          sortedRenderableFields.map((field) => {
            const rawIds = getRenderedFieldRawIds(field);
            const linkedRawFields = rawFields
              .filter((rawField) => rawIds.has(rawField.field_id))
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

            return [getFieldSortId(field), linkedRawFields];
          }),
        );

        const flattenUnitsToRaw = (unitList) => {
          const representedIds = new Set();

          const orderedRawFields = unitList.flatMap((unit) =>
            unit.fields.flatMap((field) => {
              const linkedRawFields =
                rawBlockByRenderedFieldId.get(getFieldSortId(field)) ?? [];

              linkedRawFields.forEach((rawField) => {
                representedIds.add(rawField.field_id);
              });

              return linkedRawFields;
            }),
          );

          const untouchedRawFields = rawFields
            .filter((rawField) => !representedIds.has(rawField.field_id))
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

          return [...orderedRawFields, ...untouchedRawFields].map(
            (field, index) => ({
              ...field,
              order: index,
            }),
          );
        };

        const activeUnitId = getOwnerUnitId(active.id);
        const overUnitId = getOwnerUnitId(over.id);

        if (activeUnitId && overUnitId && activeUnitId !== overUnitId) {
          const activeIndex = units.findIndex((u) => u.id === activeUnitId);
          const overIndex = units.findIndex((u) => u.id === overUnitId);
          if (activeIndex === -1 || overIndex === -1) return prev;

          return withReorderedFields(
            prev,
            flattenUnitsToRaw(arrayMove(units, activeIndex, overIndex)),
          );
        }

        if (!isFieldId(active.id) || !isFieldId(over.id)) return prev;

        const ownerUnit = units.find(
          (u) =>
            u.type === "row" &&
            u.fields.some((f) => getFieldSortId(f) === active.id),
        );
        if (!ownerUnit) return prev;
        const fromIdx = ownerUnit.fields.findIndex(
          (f) => getFieldSortId(f) === active.id,
        );
        const toIdx = ownerUnit.fields.findIndex(
          (f) => getFieldSortId(f) === over.id,
        );
        if (fromIdx === -1 || toIdx === -1) return prev;

        const reorderedRowFields = arrayMove(ownerUnit.fields, fromIdx, toIdx);
        const newUnits = units.map((u) =>
          u === ownerUnit ? { ...u, fields: reorderedRowFields } : u,
        );
        return withReorderedFields(prev, flattenUnitsToRaw(newUnits));
      };

      setDocCurrentProcessingFile(sortFields);
      setCurrentProcessingFile(sortFields);
    },
    [
      fields,
      setCurrentProcessingFile,
      setDocCurrentProcessingFile,
      handleOptionDragEnd,
    ],
  );

  const handleSectionDragEnd = useCallback(
    (event) => {
      setActiveSectionId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const visibleSections = sections.filter(
        (section) => section.fields.length,
      );
      const activeIndex = visibleSections.findIndex(
        (section) => getSectionSortId(section.section) === active.id,
      );
      const overIndex = visibleSections.findIndex(
        (section) => getSectionSortId(section.section) === over.id,
      );

      if (activeIndex === -1 || overIndex === -1) return;

      const reorderedSections = arrayMove(
        visibleSections,
        activeIndex,
        overIndex,
      );

      const sortSections = (prev) => {
        const rawFields = prev.document.tempFields.formFields ?? [];
        const rawFieldSectionIds = new Map();

        visibleSections.forEach((section) => {
          section.fields.forEach((field) => {
            getRenderedFieldRawIds(field).forEach((rawFieldId) => {
              rawFieldSectionIds.set(rawFieldId, section.section);
            });
          });
        });

        const rawFieldsBySection = new Map(
          visibleSections.map((section) => [section.section, []]),
        );
        const untouchedRawFields = [];

        rawFields
          .slice()
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .forEach((rawField) => {
            const sectionId = rawFieldSectionIds.get(rawField.field_id);
            const sectionFields = rawFieldsBySection.get(sectionId);

            if (sectionFields) {
              sectionFields.push(rawField);
            } else {
              untouchedRawFields.push(rawField);
            }
          });

        const reorderedRawFields = reorderedSections.flatMap(
          (section) => rawFieldsBySection.get(section.section) ?? [],
        );

        return withReorderedFields(
          prev,
          [...reorderedRawFields, ...untouchedRawFields].map(
            (field, index) => ({
              ...field,
              order: index,
            }),
          ),
        );
      };

      setDocCurrentProcessingFile(sortSections);
      setCurrentProcessingFile(sortSections);
    },
    [sections, setCurrentProcessingFile, setDocCurrentProcessingFile],
  );

  useEffect(() => {
    if (fillDemoRef) fillDemoRef.current = handleFillDemo;
  }, [fillDemoRef, handleFillDemo]);

  useEffect(() => {
    if (clearRef) clearRef.current = handleClear;
  }, [clearRef, handleClear]);

  const totalFieldCount =
    sections.reduce((acc, s) => acc + s.fields.length, 0) +
    directItems.length +
    (currentProcessingFile?.document?.tempFields?.formFields || []).filter(
      isGenericTablePreviewField,
    ).length;
  const visibleSections = sections.filter((section) => section.fields.length);
  const sectionSortIds = visibleSections.map((section) =>
    getSectionSortId(section.section),
  );
  const activeSection = visibleSections.find(
    (section) => getSectionSortId(section.section) === activeSectionId,
  );

  if (loading) {
    return (
      <Box sx={{ px: 1.5, display: "flex", justifyContent: "center", mt: 5.5 }}>
        <CircularProgress color="warning" size="4rem" />
      </Box>
    );
  }

  return (
    <Stack
      ref={scrollContainerRef}
      spacing={1.5}
      py={2}
      px={1}
      sx={{
        backgroundColor: "#fff",
        overflowY: "auto",
        height: "calc(100% - 130px)",
      }}
      onMouseOver={(event) => {
        if (!locateFields) return;
        const fieldKey = event.target
          .closest?.("[data-preview-field-key]")
          ?.getAttribute("data-preview-field-key");
        onHoverField?.(
          allFormField.find((field) => getFieldKey(field) === fieldKey),
        );
      }}
      onMouseLeave={() => {
        if (locateFields) onHoverField?.(null);
      }}
    >
      <DndContext
        sensors={sectionSensors}
        collisionDetection={closestCenter}
        onDragStart={({ active }) => setActiveSectionId(active.id)}
        onDragEnd={handleSectionDragEnd}
        onDragCancel={() => setActiveSectionId(null)}
      >
        <SortableContext
          items={sectionSortIds}
          strategy={verticalListSortingStrategy}
        >
          <Stack spacing={1.5}>
            {visibleSections.map((item) => (
              <SectionCard
                key={item.section}
                item={item}
                onFieldChange={handleFieldChange}
                onFieldLabelChange={handleFieldLabelChange}
                demoVersion={demoVersion}
                docFields={
                  currentProcessingFile?.document?.tempFields?.formFields
                }
                allFormField={allFormField}
                handleDragEnd={handleDragEnd}
                enableSorting={enableSorting}
                sectionDragActive={Boolean(activeSectionId)}
                locateFields={locateFields}
                locatedField={locatedField}
                onLocateField={onLocateField}
                rowSelectedFieldKeys={rowSelectedFieldKeys}
                onToggleRowField={onToggleRowField}
              />
            ))}
          </Stack>
        </SortableContext>
        <DragOverlay adjustScale={false}>
          {activeSection ? (
            <Box
              sx={{
                width: "min(420px, calc(100vw - 32px))",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                backgroundColor: "#fff",
                border: "2px solid",
                borderColor: "primary.main",
                borderRadius: "15px",
                px: 2,
                py: 1.25,
                boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.28)",
                cursor: "grabbing",
              }}
            >
              <DragIndicatorIcon
                sx={{
                  position: "absolute",
                  left: 16,
                  fontSize: "1.4rem",
                  fill: "#ababab",
                }}
              />
              <Typography variant="h6" align="center" color="primary">
                {getPreviewSectionTitle(activeSection)}
              </Typography>
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>

      <GenericTablePreview
        fields={currentProcessingFile?.document?.tempFields?.formFields}
        onCellChange={handleGenericTableCellChange}
        locateFields={locateFields}
        rowSelectedFieldKeys={rowSelectedFieldKeys}
        onToggleRowField={onToggleRowField}
        onLocateField={onLocateField}
      />

      <DirectFieldCard
        fields={directItems}
        onFieldChange={handleFieldChange}
        onFieldLabelChange={handleFieldLabelChange}
        demoVersion={demoVersion}
        docFields={currentProcessingFile?.document?.tempFields?.formFields}
        allFormField={allFormField}
        handleDragEnd={handleDragEnd}
        enableSorting={enableSorting}
        locateFields={locateFields}
        locatedField={locatedField}
        onLocateField={onLocateField}
        rowSelectedFieldKeys={rowSelectedFieldKeys}
        onToggleRowField={onToggleRowField}
      />

      {totalFieldCount === 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 8,
            gap: 1,
            color: "text.disabled",
          }}
        >
          <Typography variant="h6">No form fields found</Typography>
          <Typography variant="body2">
            Fields will appear here once the PDF is processed.
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

export default PreviewForm;
